import React, { useState, useEffect } from "react";
import { Stack, Box, Typography, Button, Skeleton, Chip } from "@mui/material";
import CreateListModal from "../../create-list-modal";
import ListPriorityModal from "../../list-priority-modal";
import AgentPriorityModal from "../../agents-priority-modal";
import { useLists } from "../api";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { BeatLoader } from "react-spinners";
import ListIcon from '@mui/icons-material/List';
import { COLORS } from "../../../../../Theme";
import ErrorHandler from "../error-handling/ErrorHandler";
import { AxiosError } from "axios";
import dayjs from "dayjs";

export default function ListBrowser({
  selected_id,
  onClick,
}: {
  selected_id: number | undefined;
  onClick: (list: List) => void;
}) {
  const [showArchived, setShowArchived] = useState(false);
  const { data: lists, isLoading: isLoadingLists, error, refetch } = useLists({
    include_archived: showArchived,
  });

  useEffect(() => {
    if(lists && lists.length && !selected_id) {
      onClick(lists[0])
    }
  },[lists])

  return (
    <Box style={{ height: "100%", padding: 15 }}>
      <Stack height="100%">
        <Stack height="80%" gap={1}>
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography style={{fontWeight: "bold", paddingLeft: 5}}>Dialing Lists</Typography>
            <Stack flexDirection="row">
              <CreateListModal />
              {/* <ListPriorityModal /> */}
              <AgentPriorityModal />
            </Stack>
          </Stack>
          <Stack overflow="auto">
            <ErrorHandler error={error as AxiosError} onRetry={refetch}/>
            {isLoadingLists ? (
              <ListBrowserLoader />
            ) : (
              lists && (
                <Stack>
                  {lists.map((l) => (
                    <ListItem
                      key={l.id}
                      list={l}
                      isSelected={l.id === selected_id}
                      onClick={onClick}
                    />
                  ))}
                </Stack>
              )
            )}
          </Stack>
        </Stack>
        <Button
          onClick={() => setShowArchived(!showArchived)}
          startIcon={<ListIcon />}
          sx={{background: "#EDEDED", color: "#000", fontWeight: "bold", textTransform: "none", paddingRight: 2}}
        >
          {showArchived ? "Hide Archived" : "View All Lists"}
        </Button>
      </Stack>
    </Box>
  );
}

const ListItem = ({
  list,
  isSelected,
  onClick,
}: {
  list: List;
  isSelected: boolean;
  onClick: (list: List) => void;
}) => {
  const startDate = dayjs(list.start_utc);
  const endDate = list.end_utc ? dayjs(list.end_utc) : dayjs();
  const daysOpen = endDate.diff(startDate, "day");
  const isArchived = !!list?.end_utc;

  const getChipColor = (days: number): "success" | "warning" | "error" => {
    if (days <= 7) return "success";
    if (days <= 14) return "warning";
    return "error";
  };

  const chipLabel = isArchived
    ? `Length: ${daysOpen} day${daysOpen !== 1 ? "s" : ""}`
    : `Open: ${daysOpen} day${daysOpen !== 1 ? "s" : ""}`;

  return (
    <Box
      sx={{
        borderBottom: isSelected ? 0 : "1px solid #ededed",
        borderLeft: isSelected ? `4px solid ${COLORS.PRIMARY}` : "",
        background: isSelected ? "#e8f8ff" : "#fff",
        borderRadius: isSelected ? 2 : 0,
        cursor: "pointer",
        padding: 1,
      }}
      onClick={() => onClick(list)}
    >
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        style={{ paddingLeft: isSelected ? 0 : 4 }}
      >
        <Stack width="100%">
          <Stack flexDirection="row" gap={2} alignItems="center" width="100%">
            <Stack width="50%" flexDirection="row" alignItems="center" gap={2}>
              <Typography style={{ fontWeight: "bold", fontSize: 13 }}>
                {list.name}
              </Typography>
            </Stack>
            <Chip
              size="small"
              label={chipLabel}
              color={isArchived ? "default" : getChipColor(daysOpen)}
              sx={{
                fontWeight: 600,
                fontSize: 10,
                width: 100,
                height: 20,
              }}
            />
          </Stack>
          <Stack flexDirection="row" alignItems="center">
            <Stack width="50%">
              <Typography
                style={{ fontSize: 11, color: "gray", fontWeight: "bold" }}
              >
                {list.count} Numbers
              </Typography>
            </Stack>
            {isArchived && (
              <Typography
                style={{ fontSize: 11, color: "gray", fontWeight: "bold", paddingTop: 2, paddingLeft: 38 }}
              >
                Archived
              </Typography>
            )}
          </Stack>
        </Stack>
        <KeyboardArrowRightIcon />
      </Stack>
    </Box>
  );
};

const ListBrowserLoader = () => {
  return (
    <Stack
    spacing={1}
    >
      <Skeleton  variant="rectangular" height={60} sx={{borderRadius: 2}} />
      <Skeleton  variant="rectangular" height={60} sx={{borderRadius: 2}} />
      <Skeleton  variant="rectangular" height={60} sx={{borderRadius: 2}} />
      <Skeleton  variant="rectangular" height={60} sx={{borderRadius: 2}} />
      <Skeleton  variant="rectangular" height={60} sx={{borderRadius: 2}} />
    </Stack>
  );
};